import type { ContractFunnelDto } from '../../api/dashboard';

import { contractFunnelSections, type ContractFunnelSection } from './contract-funnel';

/**
 * ADDITIVE (v1.9.23, T.Z105) — the contract-funnel widget, the block the owner asked
 * for right after the «Прошёл ЕВИ» card: what the T.Z101 синк договоров has said about
 * the campaign, in three sections — «подписали» (green), «не подписали» (red),
 * «не пришли в iSmart» (red). Every section is a real `<a>` to the register with its
 * `?contract=` value on the URL, so the drill-down is keyboard-reachable and
 * middle-clickable for free, exactly like the stat cards above.
 *
 * The counts are the api's (`campaign.contractFunnel`, v1.9.22) — nothing here derives
 * or caches a number. The ZERO case renders honestly: a funnel of three zeros (the sync
 * has never run, or nobody has passed the ЕВИ yet) is still a block on the page — a
 * hidden widget would read as «the feature is gone», not as «no facts yet», and the
 * sections stay the three doors into the register.
 *
 * The colours come from the same token pairs the register's status pills use
 * (`--status-active-*` green, `--status-expired-*` red), so a green on this screen and
 * a green pill on the register are the same ink — see `dashboard.css`.
 *
 * ADDITIVE (T.Z144, v1.9.37) — `format` is the «Тип площадки» axis the page is read on;
 * it rides every section's href (see `contract-funnel.ts`), so a section's count and the
 * rows its click answers stay the same list under the «Офлайн»/«Онлайн» filter too.
 */
export function ContractFunnel({
  funnel,
  format,
}: {
  funnel: ContractFunnelDto | null | undefined;
  format?: 'OFFLINE' | 'ONLINE';
}) {
  return (
    <section className="card card--flush contract-funnel" data-testid="contract-funnel" aria-label="Договоры (синк iSmart)">
      <header className="dashboard-section__head">
        <h2 className="dashboard-section__title">Договоры</h2>
        <span className="dashboard-section__hint">синк статусов договоров из iSmart</span>
      </header>
      <div className="contract-funnel__grid">
        {contractFunnelSections(funnel, format).map((section) => (
          <FunnelSection key={section.key} section={section} />
        ))}
      </div>
    </section>
  );
}

/** One section = a whole clickable link: the count, then the caption under it. */
function FunnelSection({ section }: { section: ContractFunnelSection }) {
  return (
    <a
      href={section.href}
      className={`contract-funnel__item contract-funnel__item--${section.tone}`}
      data-testid={`contract-funnel-${section.key}`}
      data-tone={section.tone}
      title={section.hint}
    >
      <span className="contract-funnel__count">{section.count}</span>
      <span className="contract-funnel__label">{section.label}</span>
    </a>
  );
}
